import {useKeycloak} from "@react-keycloak/web";
import {useState} from "react";
import {EMAIL_VERIFICATION, MAINTENANCE, MARKET_PLACE_ADMIN} from "../common/routes";
import {CUSTOMER_SHARE_MARKETPLACE_USER, EMPLOYEE_CUSTOMER_SUPPORT_VIEWER, EMPLOYEE_CUSTOMER_SUPPORT_EDITOR, CUSTOMER_MEMBER, CUSTOMER_PROSPECT, CUSTOMER_TRIAL, USER_REQUESTED_SIGNOUT} from "../common/constant";
import {getBrowser} from "../common/common";
import {checkYearEndProcessing} from "../common/configcat/functions";

export const useKeycloakFunctions = () => {

    const {keycloak, initialized} = useKeycloak();
    const [loadingTokenUpdate, setLoadingTokenUpdate] = useState(false);

    const authenticated = keycloak?.authenticated;
    const tokenParsed = keycloak?.tokenParsed;
    const userId = tokenParsed?.preferred_username;
    const country = tokenParsed?.country?.toUpperCase();

    const handleYearEndProcessing = async () => {
        if(await checkYearEndProcessing()) {
            let locale = window.location.pathname.match(/^\/([\w]{2})-([\w]{2})/g);
            if(!locale) {
                locale = "en-gb";
            } else {
                locale = locale[0];
            }
            window.location.href = locale + MAINTENANCE + "/";
            return true;
        }
    };

    const tokenUpdate = (minValidity) => {
        if (!loadingTokenUpdate) {
            setLoadingTokenUpdate(true);
            keycloak.updateToken(minValidity || process.env.KEYCLOAK_TOKEN_MIN_VALIDITY).then(function(refreshed) {
                if (refreshed) {
                    // Token refreshed
                } else {
                    // Token is valid, no refresh
                }
                setLoadingTokenUpdate(false);
            })
                .catch(function() {
                    // Failed to refresh token
                    setLoadingTokenUpdate(false);
                });
        }
    };

    const clearToken = () => {
        keycloak.clearToken();
    };

    const isTokenExpired = () => {
        return keycloak.isTokenExpired();
    };

    const login = async (redirectUri = null) => {
        if(!await handleYearEndProcessing()) {
            if (redirectUri) {
                keycloak.login({redirectUri});
            } else {
                let url = new URL(window.location.href);
                url.searchParams.delete("displayIdleDialog");
                keycloak.login({redirectUri: url.href});
            }
        }
    };

    const loginAdmin = async (username) => {
        if(!await handleYearEndProcessing()) {
            keycloak.login({
                idpHint: "hapimag-adfs",
                loginHint: username.value + "@hapimag",
                redirectUri: window.location.origin + MARKET_PLACE_ADMIN
            });
        }
    };

    const register = async(loc) => {
        if(!await handleYearEndProcessing()) {
            let url = new URL(window.location.href);
            url.searchParams.delete("displayIdleDialog");

            let redirectUri = window.location.origin + EMAIL_VERIFICATION + "/";
            redirectUri += (loc) ? "?returnUrl=" + encodeURIComponent(loc) : "";

            keycloak.register({redirectUri: redirectUri});
        }
    };

    const logout = (options) => {
        if(getBrowser() === "Safari") {
            window.localStorage.setItem(USER_REQUESTED_SIGNOUT, "1");
        }
        keycloak.logout(options);
    };

    const updateEmail = () => {
        const authUrl = keycloak.createLoginUrl() + "&kc_action=UPDATE_EMAIL";
        window.location.assign(authUrl);
    };

    const isAdmin = () => {
        return initialized && keycloak?.authenticated && (
            keycloak.hasRealmRole(EMPLOYEE_CUSTOMER_SUPPORT_VIEWER)
            || keycloak.hasRealmRole(EMPLOYEE_CUSTOMER_SUPPORT_EDITOR)
        );
    };

    const isEditorAdmin = () => {
        return initialized && keycloak?.authenticated && (
            keycloak.hasRealmRole(EMPLOYEE_CUSTOMER_SUPPORT_EDITOR)
        );
    };

    const isMarketplaceUser = () => {
        return initialized && keycloak?.authenticated && (
            keycloak.hasRealmRole(CUSTOMER_SHARE_MARKETPLACE_USER)
        );
    };

    const hasOneOfRoles = (roles) => {
        return initialized && keycloak?.authenticated && roles?.some((role) => {
            return keycloak.hasRealmRole(role);
        });
    };

    const isMember = () => {
        return initialized && keycloak?.authenticated && (
            keycloak.hasRealmRole(CUSTOMER_MEMBER)
        );
    };

    const isProspect = () => {
        return initialized && keycloak?.authenticated && (
            keycloak.hasRealmRole(CUSTOMER_PROSPECT)
        );
    };

    const isTrialMember = () => {
        return initialized && keycloak?.authenticated && keycloak.hasRealmRole(CUSTOMER_TRIAL);
    };

    return {
        keycloak,
        initialized,
        authenticated,
        tokenParsed,
        userId,
        tokenUpdate,
        clearToken,
        isTokenExpired,
        login,
        register,
        loginAdmin,
        logout,
        isAdmin,
        isEditorAdmin,
        isMarketplaceUser,
        hasOneOfRoles,
        isMember,
        isProspect,
        isTrialMember,
        updateEmail,
        country
    };
};
